
import { useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Stats {
  totalVisits: number;
  uniqueSessions: number;
  todayVisits: number;
  weekVisits: number;
  monthVisits: number;
  topPages: Array<{ page: string; count: number }>;
  deviceStats: Array<{ device: string; count: number }>;
  browserStats: Array<{ browser: string; count: number }>;
  recentVisits: Array<{
    page_path: string;
    page_title: string;
    device_type: string;
    browser: string;
    visited_at: string;
  }>;
  dailyVisits: Array<{ date: string; count: number }>;
}

export default function AdminStatsPage() {
  const [stats, setStats] = useState<Stats>({
    totalVisits: 0,
    uniqueSessions: 0,
    todayVisits: 0,
    weekVisits: 0,
    monthVisits: 0,
    topPages: [],
    deviceStats: [],
    browserStats: [],
    recentVisits: [],
    dailyVisits: [],
  });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'today' | 'week' | 'month' | 'all'>('week');

  useEffect(() => {
    fetchStats();
  }, [period]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      // Total des visites
      const { data: allVisits, count: totalCount } = await supabase
        .from('site_visits')
        .select('*', { count: 'exact' });

      // Sessions uniques
      const uniqueSessions = new Set(allVisits?.map(v => v.session_id) || []).size;

      // Visites aujourd'hui
      const { count: todayCount } = await supabase
        .from('site_visits')
        .select('*', { count: 'exact', head: true })
        .gte('visited_at', today.toISOString());

      // Visites cette semaine
      const { count: weekCount } = await supabase
        .from('site_visits')
        .select('*', { count: 'exact', head: true })
        .gte('visited_at', weekAgo.toISOString());

      // Visites ce mois
      const { count: monthCount } = await supabase
        .from('site_visits')
        .select('*', { count: 'exact', head: true })
        .gte('visited_at', monthAgo.toISOString());

      // Pages les plus visitées
      const pageVisits: { [key: string]: number } = {};
      allVisits?.forEach(visit => {
        pageVisits[visit.page_path] = (pageVisits[visit.page_path] || 0) + 1;
      });
      const topPages = Object.entries(pageVisits)
        .map(([page, count]) => ({ page, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Statistiques par appareil
      const deviceVisits: { [key: string]: number } = {};
      allVisits?.forEach(visit => {
        const device = visit.device_type || 'Inconnu';
        deviceVisits[device] = (deviceVisits[device] || 0) + 1;
      });
      const deviceStats = Object.entries(deviceVisits)
        .map(([device, count]) => ({ device, count }))
        .sort((a, b) => b.count - a.count);

      // Statistiques par navigateur
      const browserVisits: { [key: string]: number } = {};
      allVisits?.forEach(visit => {
        const browser = visit.browser || 'Inconnu';
        browserVisits[browser] = (browserVisits[browser] || 0) + 1;
      });
      const browserStats = Object.entries(browserVisits)
        .map(([browser, count]) => ({ browser, count }))
        .sort((a, b) => b.count - a.count);

      // Visites récentes
      const { data: recentVisits } = await supabase
        .from('site_visits')
        .select('page_path, page_title, device_type, browser, visited_at')
        .order('visited_at', { ascending: false })
        .limit(20);

      // Visites par jour (30 derniers jours)
      const dailyVisitsMap: { [key: string]: number } = {};
      const last30Days = allVisits?.filter(v => new Date(v.visited_at) >= monthAgo) || [];
      last30Days.forEach(visit => {
        const date = new Date(visit.visited_at).toLocaleDateString('fr-FR');
        dailyVisitsMap[date] = (dailyVisitsMap[date] || 0) + 1;
      });
      const dailyVisits = Object.entries(dailyVisitsMap)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => new Date(a.date.split('/').reverse().join('-')).getTime() - 
                        new Date(b.date.split('/').reverse().join('-')).getTime());

      setStats({
        totalVisits: totalCount || 0,
        uniqueSessions,
        todayVisits: todayCount || 0,
        weekVisits: weekCount || 0,
        monthVisits: monthCount || 0,
        topPages,
        deviceStats,
        browserStats,
        recentVisits: recentVisits || [],
        dailyVisits,
      });
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPageName = (path: string) => {
    const names: { [key: string]: string } = {
      '/': 'Accueil',
      '/about': 'À propos',
      '/services': 'Services',
      '/projects': 'Projets',
      '/gallery': 'Galerie',
      '/news': 'Actualités',
      '/testimonials': 'Témoignages',
      '/contact': 'Contact',
    };
    return names[path] || path;
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* En-tête */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Statistiques du site</h1>
            <p className="text-lg text-gray-600">Tableau de bord des visites et de l'audience</p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
          ) : (
            <>
              {/* Cartes de statistiques principales */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-6 text-white shadow-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium opacity-90">Total des visites</h3>
                    <i className="ri-eye-line text-2xl opacity-80"></i>
                  </div>
                  <p className="text-3xl font-bold">{stats.totalVisits.toLocaleString()}</p>
                  <p className="text-xs opacity-75 mt-1">Depuis le début</p>
                </div>

                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium opacity-90">Sessions uniques</h3>
                    <i className="ri-user-line text-2xl opacity-80"></i>
                  </div>
                  <p className="text-3xl font-bold">{stats.uniqueSessions.toLocaleString()}</p>
                  <p className="text-xs opacity-75 mt-1">Visiteurs différents</p>
                </div>

                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium opacity-90">Cette semaine</h3>
                    <i className="ri-calendar-week-line text-2xl opacity-80"></i>
                  </div>
                  <p className="text-3xl font-bold">{stats.weekVisits.toLocaleString()}</p>
                  <p className="text-xs opacity-75 mt-1">7 derniers jours</p>
                </div>

                <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium opacity-90">Aujourd'hui</h3>
                    <i className="ri-calendar-today-line text-2xl opacity-80"></i>
                  </div>
                  <p className="text-3xl font-bold">{stats.todayVisits.toLocaleString()}</p>
                  <p className="text-xs opacity-75 mt-1">Visites du jour</p>
                </div>
              </div>

              {/* Graphique des visites quotidiennes */}
              <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-100">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <i className="ri-line-chart-line text-teal-600 mr-2"></i>
                  Visites des 30 derniers jours
                </h2>
                <div className="overflow-x-auto">
                  <div className="flex items-end space-x-2 min-w-max h-64">
                    {stats.dailyVisits.map((day, index) => {
                      const maxCount = Math.max(...stats.dailyVisits.map(d => d.count));
                      const height = (day.count / maxCount) * 100;
                      return (
                        <div key={index} className="flex flex-col items-center group">
                          <div className="relative">
                            <div
                              className="w-8 bg-gradient-to-t from-teal-500 to-teal-400 rounded-t-lg transition-all duration-300 group-hover:from-teal-600 group-hover:to-teal-500 cursor-pointer"
                              style={{ height: `${height * 2}px`, minHeight: '4px' }}
                            ></div>
                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                              {day.count} visites
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-2 transform -rotate-45 origin-top-left whitespace-nowrap">
                            {day.date}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {/* Pages les plus visitées */}
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                    <i className="ri-file-list-line text-teal-600 mr-2"></i>
                    Pages les plus visitées
                  </h2>
                  <div className="space-y-4">
                    {stats.topPages.map((page, index) => {
                      const maxCount = stats.topPages[0]?.count || 1;
                      const percentage = (page.count / maxCount) * 100;
                      return (
                        <div key={index}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700">
                              {getPageName(page.page)}
                            </span>
                            <span className="text-sm font-bold text-teal-600">
                              {page.count} visites
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-teal-500 to-teal-400 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Statistiques par appareil */}
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                    <i className="ri-device-line text-teal-600 mr-2"></i>
                    Types d'appareils
                  </h2>
                  <div className="space-y-4">
                    {stats.deviceStats.map((device, index) => {
                      const totalDevices = stats.deviceStats.reduce((sum, d) => sum + d.count, 0);
                      const percentage = ((device.count / totalDevices) * 100).toFixed(1);
                      const colors = ['from-blue-500 to-blue-400', 'from-purple-500 to-purple-400', 'from-pink-500 to-pink-400'];
                      return (
                        <div key={index}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700 flex items-center">
                              <i className={`${device.device === 'Desktop' ? 'ri-computer-line' : device.device === 'Mobile' ? 'ri-smartphone-line' : 'ri-tablet-line'} mr-2 text-gray-500`}></i>
                              {device.device}
                            </span>
                            <span className="text-sm font-bold text-gray-900">
                              {percentage}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`bg-gradient-to-r ${colors[index % colors.length]} h-2 rounded-full transition-all duration-500`}
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <i className="ri-global-line text-teal-600 mr-2"></i>
                      Navigateurs
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {stats.browserStats.map((browser, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm font-medium text-gray-700">{browser.browser}</span>
                          <span className="text-sm font-bold text-teal-600">{browser.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Visites récentes */}
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                  <i className="ri-time-line text-teal-600 mr-2"></i>
                  Visites récentes
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Page</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Appareil</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Navigateur</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Date et heure</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recentVisits.map((visit, index) => (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                          <td className="py-3 px-4 text-sm text-gray-900 font-medium">
                            {getPageName(visit.page_path)}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs">
                              <i className={`${visit.device_type === 'Desktop' ? 'ri-computer-line' : visit.device_type === 'Mobile' ? 'ri-smartphone-line' : 'ri-tablet-line'} mr-1`}></i>
                              {visit.device_type}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">{visit.browser}</td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {new Date(visit.visited_at).toLocaleString('fr-FR')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
